import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchProgress, fetchSnapshotMap, narrateSnapshot, takeSnapshot } from "../lib/api";
import MaslowSphere from "./MaslowSphere";
import { Camera, GitCompare, PenLine } from "lucide-react";

const stamp = (s) =>
  s ? `${s.label || s.reason} · turn ${s.turn} · ${s.taken_at?.slice(11, 16)}` : "—";

/** The same subject against their own earlier sphere. */
export const Progress = ({ subject }) => {
  const [d, setD] = useState(null);
  const [frame, setFrame] = useState(0);
  const [baseline, setBaseline] = useState("");
  const [frameMap, setFrameMap] = useState(null);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [narrating, setNarrating] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(
    () =>
      fetchProgress(subject, baseline || undefined, "now")
        .then((res) => {
          setD(res);
          setFrame((res.snapshots || []).length);
        })
        .catch(() => setD(null)),
    [subject, baseline]
  );

  useEffect(() => {
    load();
  }, [load]);

  // the scrubber walks every snapshot plus the live sphere at the end
  const timeline = useMemo(
    () => (d ? [...(d.snapshots || []), { ...(d.b_meta || {}), id: "now", label: "as it stands", reason: "live" }] : []),
    [d]
  );
  const current = timeline[frame];

  useEffect(() => {
    if (!current) return;
    if (current.id === "now") {
      setFrameMap(d.map_b);
      return;
    }
    fetchSnapshotMap(current.id, subject)
      .then((r) => setFrameMap(r.map))
      .catch(() => setFrameMap(null));
  }, [current, subject, d]);

  const mark = async () => {
    setBusy(true);
    setMsg("");
    try {
      const res = await takeSnapshot(subject, label.trim() || null);
      setLabel("");
      await load();
      setMsg(
        `marked at turn ${res.snapshot.turn}, ${res.snapshot.coverage.filled}/${res.snapshot.coverage.total ?? 46} mapped`
      );
    } catch (e) {
      setMsg(e?.response?.data?.detail || "could not take a snapshot");
    } finally {
      setBusy(false);
    }
  };

  const diff = d?.diff;
  // compare_maps gives a − b; for a progress view we want movement from then to now
  const movement = useMemo(
    () =>
      diff
        ? [...diff.nodes]
            .filter((n) => n.side !== "none")
            .map((n) => ({ ...n, moved: Math.round((n.b_salience - n.a_salience) * 10) / 10 }))
            .sort((x, y) => Math.abs(y.moved) - Math.abs(x.moved))
        : [],
    [diff]
  );

  return (
    <div data-testid="admin-progress" className="space-y-7">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <p className="label">Progress · the same sphere, earlier</p>
          <p className="mt-2 max-w-xl text-xs leading-relaxed text-zinc-500">
            {d?.cadence ||
              "Captured at every phase change and every fifth scored turn, plus anything you mark."}
          </p>
        </div>
        <div className="ml-auto flex items-end gap-2">
          <input
            data-testid="progress-label-input"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="label this moment"
            className="w-56 border border-[var(--line-hi)] bg-[#0a0a0d] px-3 py-2 font-data text-[11px] text-zinc-100 outline-none placeholder:text-zinc-700"
          />
          <button
            data-testid="progress-snapshot-btn"
            disabled={busy}
            onClick={mark}
            className="btn-stark flex items-center gap-2"
          >
            <Camera size={11} strokeWidth={1.8} /> Snapshot now
          </button>
        </div>
      </div>

      {!d?.snapshots?.length && (
        <p data-testid="progress-empty" className="font-data text-[11px] text-zinc-600">
          {d?.reason || "No snapshots yet for this subject."} Take one above to start the history.
        </p>
      )}

      {d?.snapshots?.length > 0 && (
        <>
          <div className="relative h-[26rem] border border-[var(--line)]">
            {frameMap ? (
              <MaslowSphere map={frameMap} showCandidates={false} />
            ) : (
              <p className="p-5 font-data text-[11px] text-zinc-600">loading sphere…</p>
            )}
            {current && (
              <div className="panel pointer-events-none absolute left-5 top-5 max-w-[15rem] px-4 py-3">
                <p className="label">
                  {frame + 1} / {timeline.length} ·{" "}
                  {current.id === "now" ? "live" : current.reason}
                </p>
                <p
                  data-testid="progress-frame-label"
                  className="mt-2 text-sm leading-snug text-zinc-100"
                >
                  {current.label || current.reason}
                </p>
                <p className="mt-2 font-data text-[10px] leading-relaxed text-zinc-500">
                  {current.phase} · turn {current.turn} ·{" "}
                  {current.coverage?.filled ?? "—"}/{current.coverage?.total ?? 46} mapped
                  <br />
                  C {current.metrics?.predictive_confidence ?? "—"}% · gate{" "}
                  {current.metrics?.gate_open ? "OPEN" : "shut"}
                </p>
                <p className="mt-2 font-data text-[10px] text-zinc-700">
                  {current.taken_at?.slice(0, 16).replace("T", " ")}
                </p>
              </div>
            )}
          </div>

          {current && current.id !== "now" && (
            <div
              data-testid="progress-narration"
              className="border border-[var(--line)] px-5 py-5"
            >
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2">
                <p className="label flex items-center gap-2">
                  <PenLine size={11} strokeWidth={1.7} className="text-zinc-600" />
                  What moved here · the Cartographer
                </p>
                {current.narration?.against && (
                  <p className="font-data text-[10px] text-zinc-700">
                    against the previous snapshot · {current.narration.model}
                  </p>
                )}
                <button
                  data-testid="progress-renarrate-btn"
                  disabled={narrating}
                  onClick={async () => {
                    setNarrating(true);
                    setMsg("");
                    try {
                      await narrateSnapshot(current.id, subject);
                      await load();
                    } catch (e) {
                      setMsg(e?.response?.data?.detail || "could not narrate that one");
                    } finally {
                      setNarrating(false);
                    }
                  }}
                  className="label ml-auto hover:text-zinc-100"
                >
                  {narrating ? "reading…" : "read it again"}
                </button>
              </div>

              {current.narration?.headline && (
                <p
                  data-testid="progress-narration-headline"
                  className="mt-4 font-display text-xl leading-snug tracking-tight text-zinc-100"
                >
                  {current.narration.headline}
                </p>
              )}
              {current.narration?.note ? (
                <>
                  <p
                    data-testid="progress-narration-note"
                    className="mt-3 max-w-3xl text-sm leading-relaxed text-zinc-300"
                  >
                    {current.narration.note}
                  </p>
                  {current.narration.watch && (
                    <p className="mt-4 border-l border-[var(--line-hi)] pl-4 text-xs leading-relaxed text-zinc-500">
                      Next · {current.narration.watch}
                    </p>
                  )}
                </>
              ) : (
                <p className="mt-3 font-data text-[11px] text-zinc-600">
                  {current.narration?.first
                    ? "The earliest snapshot on record — there is nothing before it to compare against."
                    : "Not narrated yet. It is written automatically moments after each snapshot lands."}
                </p>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-4 border border-[var(--line)] px-4 py-4">
            <input
              type="range"
              data-testid="progress-scrubber"
              min={0}
              max={Math.max(0, timeline.length - 1)}
              value={frame}
              onChange={(e) => setFrame(Number(e.target.value))}
              className="min-w-[14rem] flex-1 accent-zinc-100"
            />
            <label className="label" htmlFor="baseline">
              Diff against
            </label>
            <select
              id="baseline"
              data-testid="progress-baseline"
              value={baseline || (d.snapshots[0]?.id ?? "")}
              onChange={(e) => setBaseline(e.target.value)}
              className="border border-[var(--line-hi)] bg-[#0a0a0d] px-3 py-2 font-data text-[11px] text-zinc-100 outline-none"
            >
              {d.snapshots.map((s) => (
                <option key={s.id} value={s.id}>
                  {stamp(s)}
                </option>
              ))}
            </select>
          </div>

          {diff && (
            <>
              <div className="grid grid-cols-2 gap-px bg-[var(--line)] sm:grid-cols-5">
                {[
                  ["New topics", diff.summary.b_only_nodes, "appeared since"],
                  ["Dropped", diff.summary.a_only_nodes, "were there, now blank"],
                  ["Carried", diff.summary.shared_nodes, "present in both"],
                  ["Mean |Δ|", diff.summary.mean_abs_delta, "salience movement"],
                  ["New edges", diff.summary.b_only_edges, "influence lines added"],
                ].map(([k, v, s]) => (
                  <div key={k} className="border border-[var(--line)] px-4 py-3">
                    <p className="label">{k}</p>
                    <p className="mt-1.5 font-data text-lg tabular-nums leading-none text-zinc-100">
                      {v}
                    </p>
                    <p className="mt-1.5 truncate font-data text-[10px] text-zinc-600">{s}</p>
                  </div>
                ))}
              </div>

              <div>
                <p className="label">
                  How they shifted · {stamp(d.a_meta)} → {d.b === "now" ? "now" : stamp(d.b_meta)}
                </p>
                <ul className="mt-4" data-testid="progress-movement">
                  {movement.slice(0, 14).map((n) => (
                    <li
                      key={n.id}
                      data-testid={`progress-row-${n.id}`}
                      className="grid grid-cols-[1fr_auto] items-baseline gap-4 border-b border-[var(--line)] py-3"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-xs text-zinc-200">
                          T{n.tier} {n.name}
                          {n.side === "b" && (
                            <span className="ml-3 font-data text-[10px] uppercase tracking-[0.14em] text-zinc-300">
                              new
                            </span>
                          )}
                          {n.side === "a" && (
                            <span className="ml-3 font-data text-[10px] uppercase tracking-[0.14em] text-zinc-500">
                              dropped
                            </span>
                          )}
                        </p>
                        <p className="mt-1 font-data text-[10px] text-zinc-600">
                          {n.a_salience} → {n.b_salience}
                          {n.valence_conflict ? " · valence flipped" : ""}
                        </p>
                      </div>
                      <span className="flex items-center gap-2 font-data text-[11px] tabular-nums text-zinc-300">
                        <GitCompare size={10} strokeWidth={1.7} className="text-zinc-700" />
                        {n.moved > 0 ? "+" : ""}
                        {n.moved}
                      </span>
                    </li>
                  ))}
                  {movement.length === 0 && (
                    <p className="font-data text-[11px] text-zinc-600">
                      Nothing has moved between these two points.
                    </p>
                  )}
                </ul>
              </div>
            </>
          )}
        </>
      )}

      {msg && (
        <p data-testid="progress-note" className="font-data text-[11px] text-zinc-400">
          — {msg}
        </p>
      )}
    </div>
  );
};

export default Progress;
